import type { App } from 'vue';
import { BaseService } from '../base';
import { IUploadOptions, OSSUpload } from './Upload';

// OSS 服务配置接口
export interface OssConfig {
  // 默认上传配置
  defaultUploadOptions?: IUploadOptions;
  // 是否启用自动清理
  enableAutoCleanup?: boolean;
  // 清理间隔（毫秒）
  cleanupInterval?: number;
  // 最大并发上传数
  maxConcurrentUploads?: number;
}

// 默认配置
const DEFAULT_CONFIG: OssConfig = {
  enableAutoCleanup: true,
  cleanupInterval: 30000, // 30秒
  maxConcurrentUploads: 3,
};

export class OssService extends BaseService {
  private config: OssConfig;
  private uploadInstances: Map<string, OSSUpload> = new Map();
  private cleanupTimer: number | null = null;

  constructor(config: OssConfig = {}) {
    super('OssService');
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // 初始化服务
  protected async onInitialize(_app?: App): Promise<void> {
    // SSR 环境下不启动自动清理，避免在服务端运行定时器
    if (import.meta.server) return;
    // 启动自动清理
    if (this.config.enableAutoCleanup) {
      this.startAutoCleanup();
    }
  }

  // 销毁服务
  protected async onDestroy(): Promise<void> {
    // 停止自动清理
    this.stopAutoCleanup();

    // 取消所有正在进行的上传
    await this.cancelAllUploads();

    // 清理所有上传实例
    this.uploadInstances.clear();
    // @ts-expect-error - 清理 config 引用
    this.config = null;
  }

  // 安装到 Vue 应用（可选）
  install(app: App): void {
    // 将 OSS 服务注入到 Vue 应用中
    app.provide('ossService', this);
  }

  /**
   * 创建上传实例
   */
  createUpload(options: IUploadOptions): OSSUpload {
    const uploadInstance = new OSSUpload(options);

    this.uploadInstances.set(uploadInstance.uid, uploadInstance);

    return uploadInstance;
  }

  /**
   * 获取上传实例
   */
  getUpload(uploadId: string): OSSUpload | undefined {
    return this.uploadInstances.get(uploadId);
  }

  /**
   * 移除上传实例
   */
  removeUpload(uploadId: string): boolean {
    return this.uploadInstances.delete(uploadId);
  }

  /**
   * 获取所有上传实例
   */
  getAllUploads(): Map<string, OSSUpload> {
    return new Map(this.uploadInstances);
  }

  /**
   * 获取活跃的上传数量
   */
  getActiveUploadCount(): number {
    return this.uploadInstances.size;
  }

  /**
   * 取消所有上传
   */
  async cancelAllUploads(): Promise<void> {
    const cancelPromises = Array.from(this.uploadInstances.values()).map(upload => {
      try {
        return upload.cancel();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to cancel upload:', error);
        return Promise.resolve();
      }
    });

    await Promise.all(cancelPromises);
  }

  /**
   * 清理已完成的上传实例
   */
  cleanupCompletedUploads(): void {
    for (const [uploadId, upload] of this.uploadInstances.entries()) {
      // 检查上传是否已完成或失败
      if (upload.state === 'completed' || upload.state === 'failed') {
        this.uploadInstances.delete(uploadId);
      }
    }
  }

  /**
   * 启动自动清理
   */
  private startAutoCleanup(): void {
    // SSR 环境下不执行
    if (import.meta.server || typeof window === 'undefined') return;

    if (this.cleanupTimer) {
      return;
    }

    this.cleanupTimer = window.setInterval(() => {
      this.cleanupCompletedUploads();
    }, this.config.cleanupInterval);
  }

  /**
   * 停止自动清理
   */
  private stopAutoCleanup(): void {
    if (this.cleanupTimer) {
      window.clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * 获取服务配置
   */
  getConfig(): OssConfig {
    return { ...this.config };
  }

  /**
   * 更新服务配置
   */
  updateConfig(newConfig: Partial<OssConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // 如果启用了自动清理，重新启动
    if (this.config.enableAutoCleanup && !this.cleanupTimer) {
      this.startAutoCleanup();
    } else if (!this.config.enableAutoCleanup && this.cleanupTimer) {
      this.stopAutoCleanup();
    }
  }
}

export const OssServiceKey = Symbol('OssService');

// 创建 OSS 服务实例
export const createOssService = (config: OssConfig = {}): OssService => {
  return new OssService(config);
};

// 全局 OSS 服务实例
let globalOssService: OssService | null = null;

// 初始化 OSS 服务
export const initOssService = async (config: OssConfig = {}): Promise<OssService> => {
  // SSR 环境下不初始化 OSS 服务（OSS 服务仅在客户端使用）
  if (import.meta.server) {
    throw new Error('OssService should not be initialized in SSR environment');
  }

  if (!globalOssService) {
    globalOssService = createOssService(config);
    await globalOssService.initialize();
  }
  return globalOssService;
};

// 获取 OSS 服务实例
export const getOssService = (): OssService => {
  if (!globalOssService) {
    throw new Error('OssService not initialized. Call initOssService first.');
  }
  return globalOssService;
};

// 便捷的上传方法
export const uploadFile = async (
  file: File & { uid: string },
  options: IUploadOptions,
): Promise<OSSUpload> => {
  const ossService = getOssService();
  const uploadInstance = ossService.createUpload(options);

  await uploadInstance.simpleUpload(file);
  return uploadInstance;
};

// 便捷的简单上传方法
export const simpleUploadFile = async (
  file: File & { uid: string },
  options: IUploadOptions,
): Promise<OSSUpload> => {
  const ossService = getOssService();
  const uploadInstance = ossService.createUpload(options);

  await uploadInstance.simpleUpload(file);
  return uploadInstance;
};

// 浏览器下载视频
export const downloadVideo = (url: string) => {
  // SSR 环境下不执行
  if (import.meta.server || typeof document === 'undefined') return;

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);

  // 添加超时清理，避免内存泄露
  const timeoutId = setTimeout(() => {
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  }, 60000); // 60秒超时

  iframe.onload = () => {
    clearTimeout(timeoutId);
    // 延迟移除，确保下载已开始
    setTimeout(() => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
    }, 1000);
  };

  // 错误处理，确保清理
  iframe.onerror = () => {
    clearTimeout(timeoutId);
    if (iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  };
};
